import React, { useState } from 'react';
import { zipCodeHasErrors } from '/Foundation/React/code/Utilities/AddressValidation';
import { fetchPromise } from './apiHelper.ts';
import { setEmailCookie } from '/Foundation/Common/code/TrackingCookieApi';
import { emailHasErrors } from '/Foundation/Common/code/Helpers/ValidationHelper';
import Input from '/Foundation/Common/code/Views/Foundation/Common/Input.js';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification.js';
import Spinner from '/Foundation/Common/code/Views/Foundation/Common/Spinner';
import { createMarkup } from '/Foundation/React/code/Utilities/HelperUtilities';
import { Translations, OrderData } from './types.ts';

interface StartProps {
  Translations: Translations;
  Market: string;
  showStartForm: boolean;
  setShowStartForm: (show: boolean) => void;
  setCurrentOrder: (order: OrderData) => void;
  scrollToTop: () => void;
  setShowHomeDeliveryDesc: (show: boolean) => void;
}

export default function Start(props: StartProps) {
  const { Translations, Market } = props;
  const [orderNumber, setOrderNumber] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOrderNoError, setShowOrderNoError] = useState(false);
  const [showZipCodeError, setShowZipCodeError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [isWithdrawalError, setIsWithdrawalError] = useState(false);

  const findOrder = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    const validated = validateFormData(orderNumber.trim(), zipCode.trim(), email.trim(), Market);
    if (!validated) {
      setIsLoading(false);
      return;
    }

    setEmailCookie(email.trim());

    const url = '/api/sitecore/withdrawals/FindOrder';
    const options: RequestInit = {
      method: 'POST',
      body: JSON.stringify({
        OrderNumber: orderNumber.trim(),
        ZipCode: zipCode.trim(),
        Email: email.trim(),
      }),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    };
    try {
      fetchPromise<any>(url, options)
        .then((response) => {
          if (response?.Success) {
            props.setCurrentOrder(response);
            props.setShowStartForm(false);
            props.setShowHomeDeliveryDesc(response.ShipMethodIsHomeDelivery);
            props.scrollToTop();
          } else if (response?.Messages.length > 0 && response?.Messages[0].Content) {
            setErrorMessage(response.Messages[0].Content);
          } else if (response?.Errors.length > 0 && typeof response?.Errors[0] === 'string') {
            setIsWithdrawalError(response.IsWithdrawalError);
            setErrorMessage(response.Errors[0]);
          } else {
            setErrorMessage(Translations.GeneralErrorMessage);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  const validateFormData = (orderNumber: string, zipCode: string, email: string, countryCode: string): boolean => {
    const validated = {
      orderNumber: validateOrderNumber(orderNumber),
      zipCode: validateZipCode(zipCode, countryCode),
      email: validateEmail(email),
    };
    for (const key in validated) {
      if (!validated[key as keyof typeof validated]) {
        return false;
      }
    }
    return true;
  };

  const validateOrderNumber = (orderNumber: string): boolean => {
    const validated = orderNumber.length > 0;
    if (!validated) {
      setShowOrderNoError(true);
      return false;
    }
    setShowOrderNoError(false);
    return true;
  };

  const validateZipCode = (zipCode: string, countryCode: string): boolean => {
    const validated = !zipCodeHasErrors(zipCode, countryCode);
    if (!validated) {
      setShowZipCodeError(true);
      return false;
    }
    setShowZipCodeError(false);
    return true;
  };

  const validateEmail = (email: string): boolean => {
    if (emailHasErrors(email)) {
      setShowEmailError(true);
      return false;
    }
    setShowEmailError(false);
    return true;
  };

  return (
    <div id="find-order-form-container" className="container col-md-8 col-lg-6 offset-md-2 offset-lg-3 mb-14">
      <h1 className="heading1 mb-8">{Translations.Heading}</h1>
      {props.showStartForm ? (
        <>
          <div className="mb-12 description" dangerouslySetInnerHTML={createMarkup(Translations.Description)} />
          <form onSubmit={(e) => findOrder(e)}>
            <div className="mb-5">
              <Input
                onBlur={(e) => validateOrderNumber(e.target.value.trim())}
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                hasError={showOrderNoError}
                errorMessage={Translations.OrderNumberError}
                label={Translations.OrderNumberLabel}
              />
            </div>
            <div className="mb-5">
              <Input
                onBlur={(e) => validateZipCode(e.target.value.trim(), Market)}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                hasError={showZipCodeError}
                errorMessage={Translations.ZipCodeError}
                label={Translations.ZipCodeOfBillingAddressLabel}
              />
            </div>
            <div className="mb-9">
              <Input
                onBlur={(e) => validateEmail(e.target.value.trim())}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                hasError={showEmailError}
                errorMessage={Translations.EmailError}
                label={Translations.EmailLabel}
              />
            </div>
            {errorMessage && (
              <div className="mb-5">
                <Notification
                  Type={'error'}
                  Size={'lg'}
                  Title={
                    isWithdrawalError ? Translations.WithdrawalOrderErrorHeader : Translations.ReturnOrderErrorHeader
                  }
                  Message={errorMessage}
                  HideCloseBtn={true}
                  ShowIcon={true}
                />
              </div>
            )}
            <button onClick={(e) => findOrder(e)} className="btn thule-btn-default w-100" disabled={isLoading}>
              <span>{Translations.ButtonFindOrder}</span>
              {isLoading && <Spinner />}
            </button>
          </form>
        </>
      ) : (
        <div className="order-loading-spinner">
          <Spinner />
        </div>
      )}
    </div>
  );
}
